<template>
  <div class="welcome-page">
    <div class="design-root">
      <div class="hero-wrapper">
        <main class="hero hero-content" :class="{ 'is-visible': !loading }">
          <div class="hero-text">
            <h1>AI让带货像呼吸一样简单</h1>
            <p>上传服装照片，一键生成商品营销视频</p>
          </div>
          <div class="cards">
            <div class="card upload-card">
              <div class="card-surface flat">
                <img
                    :src="welcomePageImage1"
                    alt="upload preview"
                    class="welcome-card-image"
                />
              </div>
              <div class="card-footer">
                <h3>图片拖动/选择</h3>
                <p>将您的创意转化为惊艳的视觉作品</p>
              </div>
            </div>

            <div class="card creative-card">
              <img
                  :src="welcomePageImage2"
                  alt="creative preview"
                  class="welcome-card-image"
              />
              <div class="card-footer highlight">
                <h3>AI构思创作</h3>
                <p>AI基于卖点,规划大纲进行创作设计</p>
              </div>
            </div>
            <div class="card video-card">
              <img
                  :src="welcomePageImage3"
                  alt="video preview"
                  class="welcome-card-image"
              />
              <div class="card-footer">
                <h3>生成视频</h3>
                <p>AI剪辑搭配音效和人物配音合成输出</p>
              </div>
            </div>
          </div>

          <button class="cta" @click="handleExperienceClick">立即体验</button>
        </main>

        <main class="hero hero-skeleton" :class="{ 'is-visible': loading }">
          <div class="hero-text">
            <div class="skeleton-line title"></div>
            <div class="skeleton-line subtitle"></div>
          </div>
          <div class="cards">
            <div class="card">
              <div class="skeleton-rect image"></div>
              <div class="card-footer">
                <div class="skeleton-line short"></div>
                <div class="skeleton-line"></div>
              </div>
            </div>
            <div class="card">
              <div class="skeleton-rect image"></div>
              <div class="card-footer">
                <div class="skeleton-line short"></div>
                <div class="skeleton-line"></div>
              </div>
            </div>
            <div class="card">
              <div class="skeleton-rect image"></div>
              <div class="card-footer">
                <div class="skeleton-line short"></div>
                <div class="skeleton-line"></div>
              </div>
            </div>
          </div>
          <div class="skeleton-rect cta-placeholder"></div>
        </main>
      </div>
    </div>
    <UploadModal
        v-if="showUploadModal"
        @close="showUploadModal = false"
        @upload="handleFileUpload"
    />
  </div>
</template>

<script setup>
import {onMounted, ref, inject} from 'vue';
import {useRouter} from 'vue-router';
import welcomePageImage1 from '@/assets/welcome_page_image_1.png';
import welcomePageImage2 from '@/assets/welcome_page_image_2.png';
import welcomePageImage3 from '@/assets/welcome_page_image_3.png';
import UploadModal from '@/components/UploadModal.vue';
import {getCvTaskCount, SUCCESS_CODE} from '@/api/api';

const loading = ref(true);
const showUploadModal = ref(false);

const router = useRouter();

// 注入显示登录窗口的方法
const showLoginModal = inject('showLoginModal');

// Cookie 工具函数（用于检查 token）
function getCookie(name) {
  const matches = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return matches ? matches[2] : null;
}

// 检查是否有 token
const hasToken = () => {
  const token = localStorage.getItem('lk-tk') || getCookie('lk-tk');
  return !!token;
};

// 处理立即体验按钮点击（只负责登录/弹出上传弹窗）
const handleExperienceClick = () => {
  if (!hasToken()) {
    // 如果没有 token，显示登录窗口
    if (showLoginModal) {
      showLoginModal();
    }
  } else {
    // 如果有 token，显示上传模态框
    showUploadModal.value = true;
  }
};

onMounted(async () => {
  // ensure skeleton shows briefly; reveal after 1s regardless of asset load
  setTimeout(() => {
    loading.value = false;
  }, 1000);

  // 页面挂载时，如果已登录则先检查任务数量，有任务则直接跳转到 /chat
  if (hasToken()) {
    try {
      const res = await getCvTaskCount();
      if (res && res.code === SUCCESS_CODE && typeof res.data === 'number' && res.data > 0) {
        router.push('/chat');
      }
    } catch (e) {
      // 查询失败时静默失败，保持在欢迎页
    }
  }
});

const handleFileUpload = () => {
  showUploadModal.value = false;
};
</script>

<style scoped>
.welcome-page {
  background: #0e0a05;
  height: calc(100vh - 65px);
  display: flex;
  justify-content: center;
  color: #faf9f6;
}

.design-root {
  position: relative;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 35%, rgba(188, 130, 58, 0.3), rgba(14, 10, 5, 0) 45%), #0e0a05;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  margin: 0 auto;
}

.hero-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero {
  position: relative;
  padding: 0 160px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.hero.is-visible {
  opacity: 1;
}

.hero-skeleton {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}

.hero.skeleton {
  gap: 24px;
}

.hero-text h1 {
  margin: 0;
  font-size: 40px;
  letter-spacing: -0.6px;
  font-weight: 600;
}

.hero-text p {
  margin: 12px 0 0;
  font-size: 18px;
  color: #e7e4df;
}

.cards {
  display: flex;
  gap: 24px;
  width: 100%;
  justify-content: center;
}

.card:hover {
  border: 1px solid #FAD87C;
}

.card {
  width: 324px;
  border-radius: 16px;
  border: 1px solid rgba(250, 216, 124, 0.45);
  background: linear-gradient(0deg, rgba(0, 0, 0, 1), rgba(99, 57, 57, 0));
  backdrop-filter: blur(22px);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.welcome-card-image {
  display: block;
  width: 100%;
  object-fit: contain;
  border-radius: 12px;
}

.card-footer {
  background: rgba(18, 16, 13, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 14px 16px;
  text-align: left;
  margin: 8px 4px 8px 4px;
}

.card-footer h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #faf9f6;
}

.card-footer p {
  margin: 6px 0 0;
  font-size: 16px;
  color: #d0ccc1;
}

.card-footer.highlight h3 {
  color: #fad87c;
}

.cta {
  margin-top: 8px;
  width: 306px;
  height: 44px;
  border: 1px solid #ddb463;
  border-radius: 8px;
  background: linear-gradient(67deg, #bc823a 0%, #f7cf73 52%, #e39c18 100%);
  color: #120f0d;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.skeleton-line,
.skeleton-rect {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin-bottom: 10px;
}

.skeleton-line::after,
.skeleton-rect::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.16), transparent);
  animation: shimmer 1.4s ease-in-out infinite;
}

.skeleton-line {
  height: 16px;
}

.skeleton-line.short {
  width: 60%;
}

.skeleton-line.title {
  width: 320px;
  height: 38px;
  margin: 0 auto;
}

.skeleton-line.subtitle {
  width: 280px;
  margin: 12px auto 0;
}

.skeleton-rect.image {
  height: 220px;
}

.skeleton-rect.cta-placeholder {
  width: 306px;
  height: 44px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  60% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.fade-hero-enter-active,
.fade-hero-leave-active {
  transition: opacity 0.4s ease;
}

.fade-hero-enter-from,
.fade-hero-leave-to {
  opacity: 0;
}

@media (max-width: 1200px) {
  .design-root {
    width: 100%;
  }

  .cards {
    flex-direction: column;
    align-items: center;
  }

  .card {
    width: 100%;
    max-width: 420px;
  }
}
</style>
